import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string, color3?: string) {
  const thirdColor = color3 ? `, ${color3}` : '';
  return `linear-gradient(to bottom, ${color1}, ${color2}${thirdColor})`;
}

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  green: string,
  green_50: string,
  aqua_50: string;
  blue: string,
  blue_50: string;
  red_orange: string,
  red_orange_50: string,
  orange_green: string,
  orange_green_50: string,
  blue_pink: string,
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

type BrandedColors = {
  yellow: string;
  paper: string;
  orange: string;
  brightGreen: string;
  skyBlue: string;
  pink: string;
  carbon: string;
  grey: string;
  darkGreenContentment: string;
  actionBlue: string;
  aquamarine: string;
  lightGrey: string;
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
    branded: BrandedColors;
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// SETUP COLORS
const BRANDED_COLORS = {
  // base
  yellow: '#F6E052',
  orange: '#FF7800',
  carbon: '#322A29',
  // additional
  darkGreenContentment: '#1A4334',
  brightGreen: '#10E57B',
  pink: '#E46AA8',
  actionBlue: '#0076FF',
  skyBlue: '#4FB8FB',
  aquamarine: '#6FF6E3',

  paper: '#FBF5EF',
  lightGrey: '#E3E3E3',
  grey: '#74635B',
};

const PRIMARY = {
  lighter: '#FBF5EF',
  light: '#FFAE66',
  main: '#FF7800',
  dark: '#CC6000',
  darker: '#994800'
};
const SECONDARY = {
  lighter: '#FBF5EF',
  light: '#FBF1B3',
  main: '#F6E052',
  dark: '#F3D722',
  darker: '#D7BC0B',
};
const CARBON = {
  lighter: '#322A29',
  light: '#322A29',
  main: '#322A29',
  dark: '#322A29',
  darker: '#322A29'
};
const CCGREY = {
  lighter: '#74635B',
  light: '#74635B',
  main: '#74635B',
  dark: '#74635B',
  darker: '#74635B'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
};
const ERROR = {
  lighter: '#FEEAEF',
  light: '#FC97AF',
  main: '#F82F5F',
  dark: '#AE2143',
  darker: '#631326',
};

export const GREY = {
  // 0: '#FFFFFF',
  // 100: '#EBEAEA',
  // 200: '#D6D4D4',
  // 300: '#C2BFBF',
  // 400: '#ADAAA9',
  // 500: '#999594',
  // 600: '#706A69',
  // 700: '#5B5554',
  // 800: '#473F3E',
  // 900: '#322A29',
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#C2BFBF', // '#E3E6EC'
  400: '#919EAB',
  500: '#999594', // '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#322A29',
  900: '#000000',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#999594', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  // branded
  green: createGradient('#F9FFC6', '#D0FFE8'),
  green_50: createGradient(alpha('#F9FFC6', 0.5), alpha('#D0FFE8', 0.5)),
  aqua_50: createGradient(alpha('#95FFCF', 0.5), alpha('#88F6E6', 0.5)),
  blue: createGradient('#9BE0FF', '#C3FFEA'),
  blue_50: createGradient(alpha('#C9EBFB', 0.5), alpha('#9BE0FF', 0.5), alpha('#8AC9FF', 0.5)),
  red_orange: createGradient('#FFC0B4', '#F8F0BF'),
  red_orange_50: createGradient(alpha('#FFC0B4', 0.5), alpha('#F8F0BF', 0.5)),
  orange_green: createGradient('#FFDFBA', '#E5FFEE'),
  orange_green_50: createGradient(alpha('#FFDFBA', 0.5), alpha('#E5FFEE', 0.5)),
  blue_pink: createGradient('#C9E5FF', '#FFCFF2'),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  carbon: { ...CARBON, contrastText: '#fff' },
  ccgrey: { ...CCGREY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  branded: { ...BRANDED_COLORS },
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[500], disabled: GREY[500_24] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action },
  },
} as const;

export default palette;
